/**
 * The central library, from the thirteen pages the institute publishes under
 * bietdvg.edu/library.
 *
 * Generated — do not hand-edit. Figures, rules and links are reproduced as
 * published; only the section framing is ours.
 */
import { DEPARTMENTS } from "./departments";
import { STAFF, type StaffMember } from "./staff";

export interface LibraryHours {
  day: string;
  /** One entry per session — the source runs them together in a single cell. */
  borrowing: string[];
  reference: string[];
}

export interface CollectionRow {
  label: string;
  value: string;
}

export interface LibraryZone {
  title: string;
  sections: string[];
}

export interface BorrowingRow {
  books: string;
  members: string;
  duration: string;
  count: string;
  overdue: string;
}

export interface ChargeRow {
  label: string;
  detail: string;
}

export interface EResource {
  name: string;
  url: string;
}

/**
 * The two resources the E-resources section leads with, called out from the
 * plain link groups below rather than buried in one of them.
 *
 * VTU E-Resources Consortium was previously on this page only as a bare
 * statistic (see LIBRARY_STATS: "E-Journals from VTU E-Resource Consortium" —
 * 1661) with no link a reader could actually follow. NDLI was not on the page
 * at all.
 */
export interface FeaturedResource {
  name: string;
  url: string;
  /** Short standing, e.g. "Subscribed" vs "Free & open" — the two read very differently. */
  tag: string;
  blurb: string;
}

export const LIBRARY_FEATURED_RESOURCES: FeaturedResource[] = [
  {
    name: "VTU E-Resources Consortium",
    url: "https://access.vtuconsortium.com/",
    tag: "Subscribed · 1,661 e-journals",
    blurb: "The library's own paid subscription, shared across VTU-affiliated colleges — full-text journal databases the library already carries the cost of.",
  },
  {
    name: "National Digital Library of India",
    url: "https://ndl.iitkgp.ac.in/",
    tag: "Free & open access",
    blurb: "A Government of India digital library, hosted by IIT Kharagpur, aggregating books, articles, theses and lectures from institutions across the country in one search.",
  },
];

export interface EResourceGroup {
  slug: string;
  title: string;
  items: EResource[];
}

export interface LibraryStaff {
  name: string;
  designation: string;
  email?: string;
  phone?: string;
  /** Staff-directory slug to borrow a portrait from — see PHOTO_SLUGS below. */
  staffSlug?: string;
}

export interface CommitteeRow {
  /** The seat, as the source names it — a role, not always one person. */
  category: string;
  role: string;
  /** Set where the seat resolves to one named person we can picture. */
  staffSlug?: string;
  /** Set on the seat held collectively by every head of department. */
  allHeads?: boolean;
}

/** The institute's own description, in its own words. */
export const LIBRARY_ABOUT: string[] = [
  "The Library & Information Centre is housed in the Administrative Block & having three floors including ground floor with a two mezzanine floors, measuring an area of 2661 sq.m. There are 9 staff members including qualified Librarian working in the library, having 600 seating capacity. To cater to the needs of the staff/ students, to have easy and speedy access to the library collection, the house keeping operations of library have been automated using EasyLib software package. It has adopted open access system and maintains Online Public Access catalogue (OPAC) to provide easy access to the stacks. The library subscribes to 139 print journals and subscribing e-journals through VTU-Consortium. It has also has a collection of nearly 4485 back volumes of periodicals.",
  "Library is an essential part of every institution which helps staff and students to enhance the knowledge through various resources such as books, Newspapers, Journals, Magazines and Electronic Library. Our institution library provides Borrowing, Book Bank, OPAC, Reference services, digital library with high speed internet facilities , Reprographic, Wi-Fi etc,.",
  "In addition to the general library, the Engineering departments have exclusive department libraries also for quick and easy access of books by staff and students",
];

/** Figures the library publishes about itself. */
export const LIBRARY_STATS = [
  { value: "1,50,401", label: "Volumes" },
  { value: "27,627", label: "Titles" },
  { value: "600", label: "Reader seats" },
  { value: "2,661", label: "Sq. metres" },
];

export const LIBRARY_HOURS: LibraryHours[] = [
  { day: "Monday to Friday", borrowing: [
      "09:00 AM - 01:00 PM",
      "02:00 PM - 05:30 PM",
    ], reference: [
      "09:00 AM - 09:00 PM",
    ] },
  { day: "Saturday", borrowing: [
      "09:00 AM - 01:30 PM",
    ], reference: [
      "09:00 AM - 09:00 PM",
    ] },
  { day: "Sunday, Holidays", borrowing: [
      "Closed",
    ], reference: [
      "09:00 AM - 01:00 PM",
      "During Examination: 09:00 AM - 9:00 PM",
    ] },
];

export const LIBRARY_COLLECTION_INTRO = "Presently Library is equipped with 150401 volumes of Books, Covering all Branches of Engineering, Basic science & General readings. The Library Resources comprises mainly with books, theses, project reports, current journals, back volumes, ISI Codes, Doctoral Thesis & Proceedings of conferences etc";

export const LIBRARY_COLLECTION: CollectionRow[] = [
  { label: "Library Collection", value: "68815" },
  { label: "SC/ST Social Walfare Book Bank", value: "5925" },
  { label: "General Book Bank", value: "40571" },
  { label: "Department Library", value: "10761" },
  { label: "E-Books", value: "24329" },
  { label: "Print Journals", value: "142" },
  { label: "E-Journals from VTU E-Resource Consortium", value: "1661" },
  { label: "Bound Volumes", value: "4485" },
  { label: "General and Subject Encyclopedias", value: "15 sets" },
  { label: "Handbooks", value: "140" },
  { label: "Phd thesis", value: "98" },
  { label: "Student Project Reports", value: "5077" },
  { label: "Conference proceeding", value: "121" },
  { label: "CDs/DVDs (Supplement to Books and Others)", value: "1894" },
  { label: "News Paper and Magazines", value: "23" },
];

export const LIBRARY_ZONES: LibraryZone[] = [
  { title: "Ground Floor", sections: [
      "Self Reading Section: To read their own books, notes etc.,",
      "Server Room",
      "Books acquisition, Technical Processing",
      "Librarian, Assistant Librarian /Office Rooms",
      "Reprographic Room",
      "Circulation Desk",
      "Digital Library",
      "Reference Section",
      "Current periodical Display",
      "News Paper and Magazine Section",
      "Cyclostyle",
    ] },
  { title: "Mezzanine Floor", sections: [
      "Book Bank Section",
      "General Reading",
    ] },
  { title: "Second Floor", sections: [
      "Borrowing Section",
      "Social welfare Book Bank Section",
      "Back Volumes Section",
      "Project Report section",
      "UPS Room",
    ] },
];

export const LIBRARY_ID_CARD = "Digital ID card will be provided to each students and staffs. It is to be used for borrowing library books and also as college Identity Card. The Detailed information and Digital ID Card Application forms are available at Library Office.";

export const LIBRARY_BORROWING: BorrowingRow[] = [
  { books: "Borrowing Section", members: "UG/PG/PhD Students", duration: "15 days", count: "02", overdue: "16th to 30th day 50 paisa per day. After 30 days onwards Rs.2/- per day till the book is returned to library." },
  { books: "Borrowing Section", members: "Teaching Staff", duration: "One year", count: "05", overdue: "—" },
  { books: "Borrowing Section", members: "Non Teaching", duration: "One year", count: "02", overdue: "—" },
  { books: "General Book Bank", members: "All the Students Members", duration: "One semester", count: "05", overdue: "—" },
  { books: "Social welfare Book Bank", members: "Only for SC/ST students", duration: "One Semester", count: "05", overdue: "—" },
];

export const LIBRARY_SERVICE_NOTES: string[] = [
  "Students can borrow two books at a time and can retain them for a fortnight. The books have to be returned before due date failing which overdue charges are levied. Teaching Faculty are given 05 books.",
  "Book Bank facility for SC/ST students is being provided for the benefit of deserving candidates with the assistance of social welfare Department, Government of Karnataka. The concerned students can keep these books for one semester.",
  "The Reference section is well equipped and having the facility for 600 readers to make reference at a time. It consists of UG Section, PG section, Digital Library and self reading Room.",
  "Internet facility is provided in Library with 15 systems connectivity.",
  "In additional to all general services, the Central Library provides various value added services including",
];

export const LIBRARY_SERVICES: string[] = [
  "Access to E-Resources",
  "Book Bank Service",
  "Career Information",
  "CD-ROM Service",
  "Circulation Service",
  "Current Awareness Service",
  "Current Content Service",
  "High speed internet service",
  "New Arrivals display",
  "Newspaper Clipping Service",
  "Newspapers & General Magazines",
  "NPTEL service",
  "OPAC - Online Public Access Catalogue",
  "Periodical Service",
  "Photocopying",
  "Previous Years VTU Question papers Service",
  "Reading Facility",
  "Reading Hall facility",
  "Reference Service",
  "SC/ST Book Bank facility",
  "User Awareness Programme: Library facilities and services orientation programme is given for the new students at the beginning of the academic year",
  "Wi-Fi service",
];

export const LIBRARY_RULES: string[] = [
  "All the readers provided with an ID card will use the same card for library access.",
  "All readers need to show their card at the entry point of library for using the library facility while check in and checkout of library.",
  "Readers are not permitted to take their personal belongings inside the library. However loose sheets are permitted for rough work.",
  "Usage of mobiles, carrying water bottle, eatables are not permitted inside the library premises.",
  "All readers need to strictly adhere to library rules and maintain silence in the library. No group discussions are permitted in the library.",
  "If any reader found taking away library documents without the permission of the library staff, the reader will be charged accordingly and the library facilities will be withdrawn.",
  "Suggestions for purchasing books for library and improving library facilities are appreciable.",
  "The borrowed books must be returned to the library within stipulated period of 15 days to avoid paying fine.",
  "All books must be returned on before the due date.",
  "Books are required to be presented physically at the Counter for return.",
  "Students are requested to confirm the generation of receipt for the overdue paid.",
  "Members must replace the book if lost with latest edition along with overdue charges.",
  "Students are required to verify the books before borrowing. Any discrepancies like missing pages etc., shall be brought to the notice of the Library staff. Otherwise books will be verified when they are returned to the library and any discrepancies found will be fined accordingly.",
  "The ID card is not transferable and must be produced whenever demanded by teacher, staff or any other college authority.",
  "In case the card is lost, kindly report immediately to the Principal / Librarian of the college in writing mentioning the details of ID Card .Members are responsible for any borrowings made against this card.",
  "Produce ID card whenever no due certificate is to be collected from the library.",
];

export const LIBRARY_CHARGES: ChargeRow[] = [
  { label: "Photocopying Charges", detail: "Single side Rs.0.75/- Both side: Rs.1.25/-" },
  { label: "Cyclostyle copes", detail: "Office work only in large scale: test question papers etc.," },
  { label: "Overdue Charges (for Students only)", detail: "1St 15 days No Fine. 2nd 15 days (16th to 30th day) Rs.0.50 per day. After 30 days onwards Rs.2/- per day till the book is returned to library." },
  { label: "Issue of Duplicate ID Card", detail: "Rs.200/- per Duplicate ID Card Rs.50/- per Renewal of ID Card" },
  { label: "Loss/Damage of Library books, Materials", detail: "Replacing the material/books or paying such compensation as fixed by the Principal/librarian." },
];

export const LIBRARY_DOS: string[] = [
  "Members should Keep belongings like bags,Aprons, books, files, Xerox copies etc., in the belongings counter at the entrance",
  "Members should Handle Library books carefully",
  "Books lost by the Borrowers have to be reported immediately in writing to the Librarian",
  "Members should Switch off cell phone",
  "Maintain Queue at circulation counter.",
  "Show the documents, which are being taken out of the Library, to the staff at the checkpoint",
  "Use the dustbins provided in the reading area and Keep the Library clean",
  "MAINTAIN SILENCE",
];

export const LIBRARY_DONTS: string[] = [
  "Keep valuables, Cash, Purses and Calculator etc., in the belongings counter",
  "Tear pages of books/Journals",
  "Write on the tables, books, Journals, other materials belonging to the library",
  "Switch on cell phone",
  "Make Noise",
  "Misplace the reading materials",
];

export const LIBRARY_E_RESOURCES: EResourceGroup[] = [
  {
    slug: "open-access-free-e-journals-e-bo",
    title: "Open Access Free E-Journals/E-Books Databases",
    items: [
      { name: "Bioline International", url: "http://www.bioline.org.br/" },
      { name: "BioMed Central", url: "http://www.biomedcentral.com/" },
      { name: "Digital Library of India", url: "http://www.dli.ernet.in/" },
      { name: "Directory of Open Access Books", url: "http://www.doabooks.org/" },
      { name: "Directory of Open Access Journals", url: "http://www.doaj.org/" },
      { name: "Down To Earth", url: "http://www.downtoearth.org.in" },
      { name: "ERIC Database", url: "http://www.eric.ed.gov/" },
      { name: "HighWire Press", url: "http://highwire.stanford.edu/" },
      { name: "IISC: Journal of the Indian Institute of Science", url: "http://journal.library.iisc.ernet.in/index.php/iisc" },
      { name: "Inadian National Science Academy", url: "http://www.insa.nic.in/" },
      { name: "Indian academy of science:", url: "http://www.ias.ac.in/Journals/Overview/" },
      { name: "Journal of Digital Information (JoDI)", url: "http://jodi.tamu.edu/" },
      { name: "Med-INDIA", url: "http://medind.nic.in/" },
      { name: "Molecular Diversity Preservation Intl.", url: "http://www.mdpi.org/" },
      { name: "National Center for Biotechnology Inf.", url: "http://www.ncbi.nlm.nih.gov/" },
      { name: "NISCAIR Research Journals - India", url: "http://www.niscair.res.in/sciencecommunication/ResearchJournals/rejour/rejour1.htm" },
      { name: "Project Gutenberg - Free e-books", url: "http://www.gutenberg.org/wiki/Main_Page" },
      { name: "PubMed Central", url: "http://www.pubmedcentral.nih.gov/" },
      { name: "Universal Digital Library - Million Books", url: "http://www.ulib.org/" },
    ],
  },
  {
    slug: "newspapers",
    title: "Newspapers",
    items: [
      { name: "Deccan Herald", url: "http://www.deccanherald.com/" },
      { name: "The Times of India", url: "http://timesofindia.indiatimes.com/" },
      { name: "The New Indian Express", url: "http://indianexpress.com/" },
      { name: "The Hindu", url: "http://www.thehindu.com/todays-paper/" },
      { name: "Prajavani", url: "http://www.prajavani.net/" },
      { name: "Vijaya Karnataka", url: "http://www.vijaykarnatakaepaper.com/" },
      { name: "Kannada Prabha", url: "http://epaper.kannadaprabha.in/landingpage" },
      { name: "Vijayavani", url: "http://epapervijayavani.in/" },
      { name: "Udayavani", url: "http://epaper.udayavani.com/" },
      { name: "Janathavani", url: "http://www.janathavani.com/" },
      { name: "Indian News papers", url: "http://www.onlinenewspapers.com/india.htm" },
      { name: "Asian Newspapers", url: "http://www.asianinfo.org/asianinfo/news/newspaper/complete.htm" },
    ],
  },
  {
    slug: "e-databases",
    title: "E-Databases",
    items: [
      { name: "Bentham Science Publishers", url: "http://www.benthamscience.com/a-z.htm" },
      { name: "Bioline International", url: "http://www.biomedcentral.com/" },
      { name: "BioMed Central (STM)", url: "http://www.bioline.org.br/" },
      { name: "Indian Journals", url: "http://indianjournals.com/ijor.aspx" },
      { name: "MedIND", url: "http://medind.nic.in/" },
      { name: "Public Library of Science (PLoS)", url: "http://www.plos.org/" },
      { name: "PubMed Central", url: "http://www.ncbi.nlm.nih.gov/pmc/" },
      { name: "Vidyanidhi", url: "http://www.vidyanidhi.org.in)" },
      { name: "Deutschen patent- und Markenamtes:", url: "https://depatisnet.dpma.de" },
      { name: "Indian Patent Database:", url: "http://www.pfc.org.in/" },
      { name: "European Patent: Esp@cenet:", url: "http://worldwide.espacenet.com/" },
      { name: "Google Patent Search:", url: "http://www.google.com/patents/" },
      { name: "U. S. Patent and Trademark Office:", url: "http://www.uspto.gov/patents/index.jsp" },
    ],
  },
  {
    slug: "career-information",
    title: "Career Information",
    items: [
      { name: "COACHING INSTITUTES FOR UGC NET", url: "http://www.careerpointchd.net" },
      { name: "COACHING INSTITUTES FOR UGC NET EXAM/ GATE", url: "http://www.axiomseducation.com/" },
      { name: "Coaching- Remedial Coaching Centre, GUG", url: "http://www.gulbargauniversity.kar.nic.in/" },
      { name: "Coaching-Training", url: "http://rajtutorials.com/" },
      { name: "CSIR-UGC NET", url: "http://203.199.141.241/online/index.jsp" },
      { name: "CSIR-UGC NET", url: "http://www.google.com/patents/" },
      { name: "Google Patent Search:", url: "http://www.csirhrdg.res.in/" },
      { name: "Karnataka Administrative Service (KAS)", url: "http://kpsc.kar.nic.in/" },
      { name: "KARNATAKA PUBLIC SERVICE COMMISSION (KPSC)", url: "http://www.kpsc.kar.nic.in/" },
      { name: "UGC NET", url: "http://www.ugc.ac.in/inside/net.html" },
      { name: "UGC NET Exam", url: "http://www.ugcnetexam.com/" },
      { name: "UGC NET Online", url: "http://www.ugcnetonline.in/" },
      { name: "UGC NET Papers", url: "http://examrace.com/UGCpaper.aspx" },
      { name: "Combined Engineering Service Examination", url: "http://www.upsc.gov.in" },
      { name: "Indian Administrative Service (IAS)", url: "http://www.upsc.gov.inandwww.upscmantra.com" },
      { name: "Indian Forest Service (IFS)", url: "http://ifs.nic.in/" },
      { name: "Bank Exams and others", url: "http://www.bankexamsindia.com/" },
      { name: "Reserve Bank Officers Exam", url: "http://www.rbi.org.in" },
      { name: "SBI Probationary Officers Exam", url: "http://www.statebankofindia.com" },
      { name: "Indian Air Force", url: "http://www.indianairforce.nic.in" },
      { name: "Indian Army", url: "http://www.indianarmy.nic.in" },
      { name: "Join Indian Army", url: "http://www.joinindianarmy.nic.in" },
      { name: "National Defense Academy", url: "http://www.nda.nic.in" },
      { name: "Education & Career, Fellowships, Loan etc.", url: "http://career.webindia123.com/career/index.html" },
      { name: "L.I.C. Assistants/ Stenos/ Typists", url: "http://59.165.134.98/chowgule/jsp/exam/competitiveexams/lic.jsp" },
      { name: "Clerical/ Officers", url: "http://ssc.nic.inandhttp://ssconline.nic.in/" },
    ],
  },
  {
    slug: "online-reference-service",
    title: "Online Reference Service",
    items: [
      { name: "English - Kannada Dictionary", url: "http://www.kannadakasturi.com/kasturiEnglishKanDictionary/start.asp" },
      { name: "Kannada - English Dictionary", url: "http://www.kannadakasturi.com/kasturiDictionary/start.asp" },
      { name: "Gazette - Karnataka State", url: "http://gazette.kar.nic.in/" },
      { name: "Gazetteer - Karnataka State", url: "http://www.kar.nic.in/gazetteer/dist_book.asp?pre_post=1&kan=2" },
      { name: "Reference information", url: "http://www.encyclopedia.com/" },
      { name: "Maps of India", url: "http://www.mapsofindia.com/" },
      { name: "Indian Constitution", url: "http://indiacode.nic.in/coiweb/welcome.html" },
    ],
  },
  {
    slug: "online-catalogue",
    title: "Online Catalogue",
    items: [
      { name: "IIT, Bombay", url: "http://www.library.iitb.ac.in/newsearchbook/opac_s.php?m_memchk_flg=&m_summary=N" },
      { name: "IIT, Delhi", url: "http://libcat.iitd.ac.in:8080/jopacv11/html/" },
      { name: "IIT, Kharagpur", url: "http://www.library.iitkgp.ernet.in/vufind/" },
      { name: "IISc, Bengaluru", url: "http://saras.library.iisc.ernet.in:8280/opac/" },
      { name: "IIMB, Bengalur", url: "http://iimb.summon.serialssolutions.com/advanced#!/advanced" },
      { name: "IIT Chennai", url: "http://www.cenlib.iitm.ac.in/docs/library/index.php" },
    ],
  },
  {
    slug: "e-syllabus-vtu",
    title: "E-Syllabus (VTU)",
    items: [
      { name: "E-Syllabus (VTU):", url: "http://vtu.ac.in/b-e-scheme-syllabus/" },
      { name: "VTU Resource", url: "http://www.vturesource.com/vtu-question-papers/" },
      { name: "Vtuplanet", url: "http://www.vtuplanet.com/" },
    ],
  },
  {
    slug: "open-educational-resources",
    title: "Open Educational Resources",
    items: [
      { name: "NPTEL", url: "https://nptel.ac.in/courses" },
      { name: "Vidyamitra", url: "https://vidyamitra.inflibnet.ac.in/index.php/" },
      { name: "Swayam", url: "https://swayam.gov.in/" },
      { name: "MIT Open course ware", url: "https://ocw.mit.edu/search/?s=department_course_numbers.sort_coursenum" },
      { name: "VTU e-Shikshana", url: "http://elearning.vtu.ac.in/esk6.php" },
      { name: "VTU e-Learning", url: "http://lms.vtu.ac.in/econtent" },
      { name: "Virtual Labs", url: "https://www.vlab.co.in/" },
      { name: "DOAB-Directory of Open Access Books", url: "https://www.doabooks.org/" },
      { name: "Open Textbook Library", url: "https://open.umn.edu/opentextbooks" },
    ],
  },
];

/**
 * As published on the library's own staff page, which prints the librarian's
 * and assistant librarian's contact details alongside their designation.
 *
 * `staffSlug` joins a row to the staff directory so the page can show that
 * person's portrait — see PHOTO_SLUGS in gen-library.py for how each join was
 * decided. Only G.K.Prasanna Kumar carries none, and falls back to a monogram.
 */
export const LIBRARY_STAFF: LibraryStaff[] = [
  { name: "Dr.K.V.Manjunatha, M.Sc, Ph.D.", designation: "Librarian", email: "kvmdvg@gmail.com", phone: "9844949956", staffSlug: "k-v-manjunatha" },
  { name: "Smt.Shakunthala G.S, M.Sc,M.Phil,", designation: "Assistant Librarian", email: "shakuntalags@rediffmail.com", phone: "9964434648", staffSlug: "g-s-shakunthala" },
  { name: "G.K.Prasanna Kumar, M.L.I.Sc", designation: "Library Assistant", phone: "9945523118" },
  { name: "Nagaraja A", designation: "Attender", staffSlug: "nagaraja-a" },
  { name: "Channabasappa", designation: "Peon", staffSlug: "channabasappa-k" },
  { name: "Kenchappa", designation: "Attender", staffSlug: "kenchappa-n" },
  { name: "Govindraj", designation: "Attender", staffSlug: "govindaraju-h" },
  { name: "Smt. Pushpa", designation: "Attender", staffSlug: "s-pushpa" },
  { name: "Nirmala K P", designation: "Peon", staffSlug: "nirmala-k-p" },
];

/**
 * The seats on the committee, as the source names them. Two resolve to a single
 * named person; the third is held by every head of department at once.
 */
export const LIBRARY_COMMITTEE: CommitteeRow[] = [
  { category: "Principal", role: "Chairman", staffSlug: "h-b-aravind" },
  { category: "All Heads of the Department", role: "Member", allHeads: true },
  { category: "Librarian", role: "Ex-Office Secretary", staffSlug: "k-v-manjunatha" },
];

/**
 * The one manual the library publishes for readers.
 *
 * `url` is a media-bucket key, not a link: the PDF was pulled off the old site
 * and re-published through scripts/ingest-docs.py, so it is served from our own
 * document CDN and no longer depends on bietdvg.edu staying up. Resolve it with
 * docUrl().
 */
export const LIBRARY_USER_MANUAL = {
  name: "Library User Manual",
  url: "sites/biet/docs/library-user-manual.pdf",
  /** Size of the published PDF, so the card can state it honestly. */
  bytes: 483327,
} satisfies EResource & { bytes: number };

/** A library row's staff-directory record. Undefined when unjoined. */
export function libraryPerson(row: { staffSlug?: string }): StaffMember | undefined {
  if (!row.staffSlug) return undefined;
  return STAFF.find((s) => s.slug === row.staffSlug);
}

/**
 * Everyone holding the committee's collective "all heads of department" seat.
 *
 * Taken from the departments rather than from staff.ts designations: the
 * employee record still carries former heads (three rows read "Professor &
 * Head, CV"), whereas each department page publishes exactly the one head it
 * has now. 19 of the 21 departments name one — SH and IOT publish none.
 */
export const LIBRARY_COMMITTEE_HEADS = DEPARTMENTS.flatMap((d) =>
  d.head ? [{ code: d.code, name: d.head.name, photo: d.head.photo }] : [],
);
